import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import ToolCard from "../components/ToolCard";
import * as api from "../services/api";
import { track } from "../services/events";
import { useSiteSettings } from "../hooks/useSiteSettings";

const ANNOUNCEMENTS = [
  { label: "New", text: "Budget Planner v2: household sync, transaction history & 11 planning modes", link: "/tools/budget-planner" },
  { label: "Guide", text: "How to run a Assess → Build → Scale consulting engagement", link: "/consulting" },
  { label: "Trust", text: "Read our full Trust & Security policy — data handling, encryption & privacy", link: "/trust" },
];

// Animated counter hook
function useCounter(target: number, duration = 1800) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (target === 0) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const steps = 60;
          const increment = target / steps;
          let current = 0;
          const interval = setInterval(() => {
            current = Math.min(current + increment, target);
            setCount(Math.floor(current));
            if (current >= target) clearInterval(interval);
          }, duration / steps);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return { count, ref };
}

const categories = [
  { name: "Finance", icon: "💰", color: "from-green-500 to-emerald-600", bg: "bg-green-50", border: "border-green-200", slug: "finance" },
  { name: "Analytics", icon: "📊", color: "from-blue-500 to-cyan-600", bg: "bg-blue-50", border: "border-blue-200", slug: "analytics" },
  { name: "Marketing", icon: "📣", color: "from-orange-500 to-red-500", bg: "bg-orange-50", border: "border-orange-200", slug: "marketing" },
  { name: "Design", icon: "🎨", color: "from-purple-500 to-pink-600", bg: "bg-purple-50", border: "border-purple-200", slug: "design" },
  { name: "Developer", icon: "⚡", color: "from-gray-600 to-slate-700", bg: "bg-gray-50", border: "border-gray-200", slug: "developer" },
  { name: "Productivity", icon: "🚀", color: "from-teal-500 to-cyan-600", bg: "bg-teal-50", border: "border-teal-200", slug: "productivity" },
];

const steps = [
  { num: "01", title: "Pick a tool", desc: "Browse 12 professional tools across 6 categories — no signup needed to explore." },
  { num: "02", title: "Run instantly", desc: "Fill in your inputs and get AI-powered results in seconds. No waiting, no fluff." },
  { num: "03", title: "Save & share", desc: "Export your results, save runs to your dashboard, and share with your team." },
];

function AnnouncementBanner() {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % ANNOUNCEMENTS.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  if (!visible) return null;

  const a = ANNOUNCEMENTS[index];
  return (
    <div className="bg-indigo-700 text-white text-sm py-2 px-4 flex items-center justify-center gap-3 relative">
      <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full shrink-0">{a.label}</span>
      <Link to={a.link} className="hover:underline underline-offset-2 line-clamp-1 text-center">{a.text}</Link>
      <div className="flex gap-1 shrink-0 ml-2">
        {ANNOUNCEMENTS.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            className={`w-1.5 h-1.5 rounded-full transition-all ${i === index ? "bg-white" : "bg-white/40"}`}
            aria-label={`Announcement ${i + 1}`}
          />
        ))}
      </div>
      <button
        onClick={() => setVisible(false)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-base leading-none"
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

function StatBlock({ value, label }: { value: number; label: string }) {
  const { count, ref } = useCounter(value);
  return (
    <div ref={ref} className="text-center">
      <p className="text-5xl font-extrabold text-indigo-600 tabular-nums">
        {count.toLocaleString()}{value >= 1000 ? "+" : ""}
      </p>
      <p className="text-gray-500 mt-2 font-medium">{label}</p>
    </div>
  );
}

export default function Home() {
  const [tools, setTools] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const siteSettings = useSiteSettings();

  useEffect(() => {
    api.getFeaturedTools().then((d) => setTools(d.tools)).catch(() => {});
    api.getMarketingStats().then((d) => setStats(d)).catch(() => {});
  }, []);

  return (
    <div className="overflow-x-hidden">

      {/* ─── Announcement Banner ─── */}
      <AnnouncementBanner />

      {/* ─── Hero ─── */}
      <section className="relative text-white overflow-hidden" style={{ background: "linear-gradient(to bottom right, var(--brand-from), var(--brand-to))" }}>
        {/* decorative blobs */}
        <div className="absolute top-[-80px] left-[-80px] w-72 h-72 bg-white/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-[-100px] right-[-60px] w-96 h-96 bg-pink-500/20 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 py-28 text-center">
          {/* trust badge */}
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-8">
            <span className="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            No credit card required · Start free today
          </div>

          <h1 className="text-5xl sm:text-6xl font-extrabold leading-tight mb-6 tracking-tight">
            {(siteSettings.hero_headline.includes(",")
              ? siteSettings.hero_headline.split(",")[0]
              : siteSettings.hero_headline)},<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-pink-300">
              {siteSettings.hero_headline.includes(",")
                ? siteSettings.hero_headline.split(",").slice(1).join(",").trim()
                : ""}
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-indigo-100 mb-10 max-w-2xl mx-auto leading-relaxed">
            {siteSettings.hero_subtext}
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/register"
              className="inline-block bg-white text-indigo-700 font-bold px-9 py-4 rounded-xl hover:bg-indigo-50 transition shadow-lg hover:shadow-xl text-base"
            >
              {siteSettings.cta_primary_text}
            </Link>
            <Link
              to="/tools"
              className="inline-block border-2 border-white/60 text-white font-semibold px-9 py-4 rounded-xl hover:bg-white/10 transition text-base"
            >
              {siteSettings.cta_secondary_text}
            </Link>
          </div>

          {/* social proof strip */}
          <div className="mt-12 flex flex-wrap justify-center gap-6 text-sm text-indigo-100">
            <span>✅ Instant access</span>
            <span>✅ Privacy-first</span>
            <span>✅ 14-day money-back</span>
            <span>✅ No lock-in</span>
          </div>
        </div>
      </section>

      {/* ─── Infrastructure trust strip ─── */}
      <section className="py-8 bg-gray-50 border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-center text-xs font-semibold text-gray-400 uppercase tracking-widest mb-5">Built on trusted, battle-tested infrastructure</p>
          <div className="flex flex-wrap justify-center gap-6 items-center">
            {[
              { name: "Cloudflare Workers", badge: "Edge Compute" },
              { name: "Cloudflare D1", badge: "Database" },
              { name: "Stripe", badge: "Payments · PCI DSS L1" },
              { name: "React 18", badge: "Frontend" },
              { name: "TypeScript", badge: "Type-Safe" },
            ].map((p) => (
              <div key={p.name} className="flex flex-col items-center px-5 py-3 bg-white rounded-xl border border-gray-200 shadow-sm min-w-[120px]">
                <p className="font-bold text-gray-800 text-sm">{p.name}</p>
                <p className="text-xs text-indigo-600 font-medium mt-0.5">{p.badge}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How it works ─── */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm mb-3">How it works</p>
          <h2 className="text-4xl font-bold mb-16 text-gray-900">From idea to result in under a minute</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {steps.map((s) => (
              <div key={s.num} className="relative flex flex-col items-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xl font-extrabold mb-5 shadow-lg">
                  {s.num}
                </div>
                <h3 className="font-bold text-lg mb-2 text-gray-900">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Categories ─── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm text-center mb-3">Categories</p>
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">Everything your workflow needs</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/tools?category=${cat.slug}`}
                className={`group flex flex-col items-center gap-3 p-5 rounded-2xl border ${cat.bg} ${cat.border} hover:shadow-md transition-all hover:-translate-y-1`}
              >
                <span className="text-3xl">{cat.icon}</span>
                <span className="text-sm font-semibold text-gray-700 group-hover:text-indigo-600 transition-colors">{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Featured Tools ─── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
            <div>
              <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm mb-2">Popular Tools</p>
              <h2 className="text-4xl font-bold text-gray-900">Start with what works</h2>
            </div>
            <Link to="/tools" className="text-indigo-600 font-semibold hover:text-indigo-800 whitespace-nowrap">
              See all 12 tools →
            </Link>
          </div>
          {tools.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map((i) => (
                <div key={i} className="border rounded-2xl p-6 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-20 mb-3" />
                  <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-full" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tools.slice(0, 6).map((t) => (
                <ToolCard key={t.slug} tool={t} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ─── Stats ─── */}
      <section className="brand-gradient py-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { value: stats?.tool_count ?? 12, label: "Professional Tools", suffix: "+", sub: "Ready to run — no setup" },
              { value: 6, label: "Core Categories", suffix: "", sub: "Finance, design, docs & more" },
              { value: 100, label: "Free to Start", suffix: "%", sub: "No credit card required" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-5xl font-extrabold text-white tabular-nums">{s.value.toLocaleString()}{s.suffix}</p>
                <p className="text-indigo-200 mt-2 font-medium">{s.label}</p>
                <p className="text-indigo-300/70 text-sm mt-1">{s.sub}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-indigo-200/90 text-xs mt-8">
            Startup transparency: we publish only verifiable platform metrics.
          </p>
        </div>
      </section>

      {/* ─── Brand System ─── */}
      <section className="py-24 bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-cyan-300 font-semibold uppercase tracking-widest text-sm mb-3">Brand Identity</p>
            <h2 className="text-4xl font-bold">Built around dimensional clarity</h2>
            <p className="text-slate-300 mt-4 max-w-2xl mx-auto">
              The 9nd mark represents layered insight: one symbol that moves from data to action.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            <div className="rounded-3xl border border-slate-800 bg-black p-8 flex items-center justify-center min-h-[280px]">
              <img src="/assets/brand/logo-full.png" alt="9nd full logo" className="w-full max-w-lg object-contain" loading="lazy" decoding="async" />
            </div>

            <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-8">
              <h3 className="text-xl font-bold mb-5">Icon System</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                {[
                  { src: "/assets/brand/favicon-32x32.png", label: "Favicon" },
                  { src: "/assets/brand/apple-touch-icon.png", label: "Apple" },
                  { src: "/assets/brand/icon-192.png", label: "Android" },
                ].map((item) => (
                  <div key={item.label} className="rounded-2xl border border-slate-700 bg-black/40 p-4">
                    <img src={item.src} alt={`${item.label} icon`} className="w-14 h-14 mx-auto object-contain" loading="lazy" decoding="async" />
                    <p className="text-xs text-slate-300 mt-3 uppercase tracking-wider">{item.label}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-slate-300 mt-6 leading-relaxed">
                Icons are generated from the same core mark to keep platform touchpoints consistent across tabs, homescreens, and social previews.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Client Outcomes ─── */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm mb-3">Client Outcomes</p>
            <h2 className="text-3xl font-bold text-gray-900">Real results from real workflows</h2>
            <p className="text-gray-500 mt-3 max-w-lg mx-auto text-sm">From freelancers to growing teams — here's what professionals have achieved with 9nd.org.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {[
              {
                tag: "Finance",
                metric: "Hours of budget admin eliminated",
                desc: "Freelance designers using our Budget Planner report eliminating manual monthly spreadsheet work by combining zero-based budgeting and envelope tracking in one workflow.",
                icon: "💰",
              },
              {
                tag: "Marketing",
                metric: "Fewer SaaS subscriptions needed",
                desc: "Growth marketers consolidate color palettes, font pairings, and copy generation into a single platform — cutting redundant tool costs from their monthly stack.",
                icon: "📣",
              },
              {
                tag: "Development",
                metric: "Faster initial project scoping",
                desc: "Software engineers using our analytics and estimation tools cut the back-and-forth of scoping sessions by arriving with structured outputs instead of rough estimates.",
                icon: "⚡",
              },
            ].map((o) => (
              <div key={o.tag} className="rounded-2xl border border-gray-100 p-7 shadow-sm hover:shadow-md transition bg-white">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">{o.icon}</span>
                  <span className="text-xs font-bold bg-indigo-50 text-indigo-600 px-2.5 py-1 rounded-full">{o.tag}</span>
                </div>
                <p className="text-xl font-extrabold text-gray-900 mb-3 leading-tight">{o.metric}</p>
                <p className="text-gray-500 text-sm leading-relaxed">{o.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/consulting" className="inline-block text-sm font-semibold text-indigo-600 hover:text-indigo-800 border border-indigo-200 px-6 py-2.5 rounded-xl hover:bg-indigo-50 transition">
              Need a consulting engagement? →
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Insights Path ─── */}
      <section className="py-20 bg-slate-950 text-white border-t border-slate-900">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-cyan-300 font-semibold uppercase tracking-widest text-sm mb-3">Keep Exploring</p>
            <h2 className="text-4xl font-bold">Build skills, not just outputs</h2>
            <p className="text-slate-300 mt-3 max-w-2xl mx-auto text-sm">
              Move from one-off tool usage to repeatable growth with curated insights, playbooks, templates, and learning paths.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-5">
            {[
              { to: "/insights", title: "Insights Hub", desc: "Curated trends and practical guidance." },
              { to: "/ai-readiness", title: "AI Readiness", desc: "Assess if your team is ready to scale AI." },
              { to: "/playbooks", title: "Playbooks", desc: "Step-by-step implementation paths." },
              { to: "/academy", title: "Learning Center", desc: "Structured capability building for teams." },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() =>
                  track("home_keep_exploring_click", {
                    destination: item.to,
                    title: item.title,
                    section: "keep_exploring",
                  })
                }
                className="rounded-2xl border border-slate-800 bg-slate-900 p-5 hover:border-cyan-500/50 hover:shadow-lg hover:shadow-cyan-900/20 transition"
              >
                <p className="font-bold text-white mb-2">{item.title}</p>
                <p className="text-xs text-slate-300 leading-relaxed">{item.desc}</p>
                <p className="text-xs text-cyan-300 font-semibold mt-3">Explore →</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Trust / Testimonials ─── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm mb-3">Trusted by professionals</p>
          <h2 className="text-4xl font-bold mb-14 text-gray-900">Real people. Real results.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { quote: "The budget planner alone saved me 3 hours every month. The whole platform is just fast and clean.", name: "Alex R.", role: "Freelance Designer" },
              { quote: "I replaced 4 different SaaS subscriptions with 9nd. It handles everything I need without the clutter.", name: "Sarah M.", role: "Growth Marketer" },
              { quote: "As a developer, the code tools are surprisingly powerful. The API access on Pro is a game-changer.", name: "James L.", role: "Software Engineer" },
            ].map((t) => (
              <div key={t.name} className="bg-white rounded-2xl p-7 shadow-sm border border-gray-100 text-left hover:shadow-md transition">
                <div className="flex mb-4">
                  {[1,2,3,4,5].map((s) => <span key={s} className="text-yellow-400 text-base">★</span>)}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-5">"{t.quote}"</p>
                <div>
                  <p className="font-bold text-gray-900 text-sm">{t.name}</p>
                  <p className="text-gray-500 text-xs">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-28 bg-white text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-4xl sm:text-5xl font-extrabold mb-5 text-gray-900 leading-tight">
            Your workflow deserves<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">better tools.</span>
          </h2>
          <p className="text-gray-500 mb-10 text-lg">Start free. No credit card. Instant access to 12 professional tools.</p>
          <Link
            to="/register"
            className="inline-block bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold px-12 py-5 rounded-xl hover:opacity-90 transition shadow-xl text-lg"
          >
            Start for Free →
          </Link>
          <p className="text-sm text-gray-400 mt-5">Join hundreds of professionals already using 9nd.org</p>
        </div>
      </section>

    </div>
  );
}